<template>
  <div class="card">

    <!-- User Info -->
    <UserInfo :uid="answer.uid" :unix="answer.time_created" :nps_override="answer.answer_type === 'nps' ? answer.answer : null" />

    <!-- Question Text -->
    <h3
    v-if="is_respondent_page && question_title"
    :class="{ expanded: is_question_expanded }"
    class="question-title"
    @click="is_question_expanded=!is_question_expanded">{{ answer.question_index + 1 }}. {{ question_title }}</h3>

    <!-- VIDEO ANSWERS -->
    <template v-if="answer.answer_type === 'video'">

      <!-- Video Player -->
      <video width="100%" controls preload="metadata" @play="onPlay()" :src="answer.answer" ></video>

      <!-- Video Transcription -->
      <textarea-autosize
        class="transcript"
        ref="textArea"
        v-model="transcript_edited"
        placeholder='No transcript'
        :disabled="is_demo_survey"
        :min-height="20"
        :max-height="350"
      />

    </template>



    <!-- AUDIO ANSWERS -->
    <template v-if="answer.answer_type === 'audio'">

      <!-- Audio Player -->
      <audio controls preload="metadata">
        <source :src="answer.answer" type="audio/mpeg">
      </audio>

      <!-- Audio Transcription -->
      <textarea-autosize
        class="transcript"
        ref="textArea"
        v-model="transcript_edited"
        placeholder='No transcript'
        :disabled="is_demo_survey"
        :min-height="20"
        :max-height="350"
      />

    </template>



    <!-- IMAGE ANSWERS -->
    <template v-if="answer.answer_type === 'image'">

      <!-- Single Image -->
      <img v-if="answer.answer" :src="answer.answer" @click="showModal(answer.answer)"/>

      <!-- Image preview modal -->
      <a-modal
        v-model="is_modal_open"
        @ok="hideModal"
        >
        <img :src="shown_image" class="modal-pic"/>
      </a-modal>


      <div v-if="answer.labels" class="label-container">
        <div v-for="label of answer.labels">
          <p>{{ label }}</p>
        </div>
      </div>


      <!-- OCR Transcription -->
      <textarea-autosize
        class="transcript"
        ref="textArea"
        v-if="answer.transcription"
        v-model="transcript_edited"
        placeholder='No OCR'
        :min-height="20"
        :max-height="350"
      />

    </template>



    <!-- TEXT ANSWERS -->
    <template v-if="answer.answer_type === 'text' || answer.answer_type === 'nps'">
      <p v-if="answer.answer || answer.answer == '0'" class="text-answer"> {{ _.capitalize(answer.answer) }}</p>
    </template>


    <!-- Multi Choice ANSWERS -->
    <template v-if="answer.answer_type === 'multi_choice'">
      <p class="multi-choice-answer">📊 {{ multi_choice_answer_text }}</p>
    </template>


    <!-- LOCATION ANSWERS -->
    <template v-if="answer.answer_type === 'location'">
      <iframe
        width="270"
        height="310"
        style="border:0"
        loading="lazy"
        allowfullscreen
        :src="`https://www.google.com/maps/embed/v1/view?key=AIzaSyA3d8CdfjaQ2C_NlA-Dqf4zlIHzGbnYzaQ&center=${answer.answer}&zoom=17`">
      </iframe>
    </template>



    <!-- Bottom Footer -->
    <div
    v-if="!is_demo_survey"
    class="bottom-footer">

      <!-- Comment and Save Edits -->
      <div class="footer-left">

        <!-- Comments Button -->
        <p class="comment-count">{{ answer.comments && answer.comments.length ? answer.comments.length : '' }}</p>
        <a-icon type="message" theme="filled" class="comment-btn" @click="openComments"/>

        <!-- Save Transcript Edits Button -->
        <template v-if="['audio', 'video'].includes(answer.answer_type) || (answer.answer_type === 'image' && answer.transcription !== undefined)">
          <a-icon @click="focusTranscriptTextarea" v-if="transcript === transcript_edited" class="edit-btn" type="edit" theme="filled" />
          <p @click="saveTranscriptEdits" v-if="transcript !== transcript_edited" class="save-edits-btn">Save Edits</p>
        </template>

      </div>

      <!-- Delete and Favorite Buttons -->
      <div>
        <template>
          <a-popconfirm title="Remove this answer?" @confirm="confirmDelete" okText="Yes" cancelText="Cancel">
            <a-icon class="delete-btn" type="delete" theme="filled" />
          </a-popconfirm>
        </template>
        <a-rate class="favorite-btn" :value="answer.is_favorited ? 1: 0" :count="1" @change="toggleFavorite" />
      </div>

    </div>
    <!-- End of Bottom Footer -->

  </div>
</template>

<script>
import UserInfo from '@/components/answers/components/UserInfo.vue'
export default {
  name: 'answerCard',
  props: {
    answer: Object,
    is_respondent_page: Boolean
  },
  components: {
    UserInfo
  },

  data: function () {
    let transcript = this.answer.transcription || '';
    return {
      is_text_expanded: false,
      is_question_expanded: false,
      is_modal_open: false,
      shown_image: null,
      transcript,
      transcript_edited: transcript,
    }
  },

  computed: {

    survey() {
      if (!this.$store.state.surveys) { return null };
      return this.$store.state.surveys[this.$route.params.survey_index];
    },

    question_title() {
      if (!this.survey) { return null };
      return this.survey.questions[this.answer.question_index].text.en || this.survey.questions[this.answer.question_index].text;
    },

    // Multi-choice
    multi_choice_answer_text() {
      if (this.survey.questions) {
        let multi_choice_option = this.survey.questions[this.answer.question_index].multi_choice[this.answer.answer];
        return multi_choice_option.en || multi_choice_option.text || multi_choice_option;
      } else {
        return null;
      };
    },

    is_demo_survey() {
      return this.survey ? this.survey.id === "cNUZcoeTjtX1h9vUEama" : false;
    },

  },

  methods: {

    toggleFavorite(val) {
      this.$store.dispatch('toggleIsFavorited', {
        survey_index: this.$route.params.survey_index,
        answer_id: this.answer.id,
        is_favorited: (val === 1)
      });
      analytics.track('Answer Favorited');
    },

    confirmDelete(e) {

      let respondent_id = this.answer.uid;
      let survey_index = this.$route.params.survey_index;
      let answer_id = this.answer.id;
      let question_index = this.answer.question_index;

      this.$store.dispatch('deleteAnswer', [survey_index, question_index, answer_id]);
      this.$message.success(`Answer removed`, 3); // Show message for 3 seconds
      analytics.track('Answer Deleted', { survey_id: this.survey.id });

    },

    openComments() {
      this.$store.commit('setCommentsAnswerID', this.answer.id);
    },

    focusTranscriptTextarea() {
      this.$refs.textArea.$el.focus();
    },

    saveTranscriptEdits() {
      this.transcript = this.transcript_edited;
      let survey_index = this.$route.params.survey_index;
      let answer_id = this.answer.id;
      this.$store.dispatch('editAnswerTranscript', { survey_index, answer_id, edited_transcript: this.transcript_edited });
      this.$message.success(`Edits saved`, 2); // Show success message
    },

    // For auto-playing multiple videos
    onPlay: function () {
      analytics.track('Video Played');
    },

    // Image Modals
    showModal(url) {
      this.is_modal_open = true
      this.shown_image = url
    },
    hideModal(e) {
      this.is_modal_open = false
      this.shown_image = null
    }

  }
}
</script>

<style scoped lang="scss">

.card{
  width: 310px;
  padding: 20px;
  margin-top: 20px;
  background-color: white;
  border: 1px solid #ECECEC;
  border-radius: 6px;
  box-shadow: 0 0 8px 0 rgba(0,0,0,.08);
  font-family: "Montserrat", sans-serif;
}

// Question Title on Respondent's Page
h3.question-title{
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  margin-bottom: 15px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  cursor: pointer;
  &.expanded{
    -webkit-line-clamp: 20;
  }
}

video{
  background-color: black;
  //min-height: 520px;
}

audio{
  margin-top: 4px;
  margin-bottom: 10px;
  height: 45px;
  width: 100%;
}

img{
  width: 100%;
  border-radius: 4px;
  cursor: pointer;
}

p.text-answer,
p.multi-choice-answer{
  font-size: 22px;
  font-weight: 500;
  margin-bottom: 6px;
  overflow-wrap: break-word;
  hyphens: auto;
}
p.multi-choice-answer{
  text-align: center;
}


hr{
  margin-top: 20px;
  margin-bottom: 20px;
  opacity: .4;
}

textarea.transcript{
  margin-top: 10px;
  text-align: left;
  padding: 5px;
  width: 100%;
  color: #0D303F;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  outline: none;
  border: none;
  border-radius: 6px;
  &:focus{
    background-color: rgb(248,248,248);
  }
}

.label-container{
  margin-top: 20px;
  & > div{
    display: inline-block;
    padding: 7px 10px;
    margin-right: 8px;
    border: 1px solid rgb(210,210,210);
    border-radius: 4px;
    box-shadow: 0 0 2px 0 rgba(0,0,0,.15);
    p{
      font-size: 12px;
      font-weight: 600;
      color: rgb(40,40,40);
      margin: 0px;
    }
  }
}


.bottom-footer{

  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 10px;

  &.expanded{
    margin-top: 60px;
  }
  &.superExpanded{
    margin-top: 100px;
  }

  .footer-left{
    width: 120px;
    & > *{
      display: inline-block;
    }
    .comment-count{
      margin: 0;
      margin-right: 5px;
      transform: translate(0px, 6px);
      font-size: 13px;
      font-weight: 600;
    }
    .comment-btn{
      font-size: 16px;
      display: inline-block;
      vertical-align: bottom;
      transform: translate(0px, 3px);
      opacity: .5;
      transition: all 0.3s ease;
      &:hover{
        opacity: .75;
        cursor: pointer;
      }
    }
  }

  .edit-btn{
    font-size: 19px;
    opacity: .4;
    margin-left: 17px;
    transform: translateY(9px);
    &:hover{
      cursor: pointer;
      opacity: .8;
      transition: all 0.3s ease;
    }
  }

  .save-edits-btn{
    font-weight: 600;
    font-size: 13px;
    color: #17C890;
    height: 21px;
    margin: 0;
    margin-left: 15px;
    transform: translateY(7px);
    cursor: pointer;
    opacity: .9;
    transition: all 0.3s ease;
    &:hover{
      opacity: 1;
    }
  }

  .delete-btn{
    font-size: 16px;
    opacity: .4;
    margin-right: 18px;
    &:hover{
      cursor: pointer;
      opacity: .55;
      transform: scale(1.075, 1.075);
      transition: all 0.3s ease;
    }
  }
}

/* For image carousel */
.ant-carousel {
  .slick-slide img {
    border: 5px solid #fff;
    display: block;
    margin: auto;
    max-width: 100%;
  }
  .slick-thumb li img {
    width: 45px;
    height: 40px;
    object-fit:cover;
    filter: grayscale(100%);
    border: 1px solid rgb(200,200,200);
    transform: translateY(18px) !important;
  }
  .slick-thumb li.slick-active img {
    filter: grayscale(0%);
  }
}



@media only screen
and (min-device-width : 320px)
and (max-device-width : 480px) {
.card{
  width: 100%;
}
}

@media only screen and (min-device-width : 768px) and (max-device-width : 1024px) and (orientation : portrait) {
  .card{
    width: 100%;
  }
  .footer-left{
    width: 50% !important;
  }
}
</style>
